/**
 * The card Customize dialog's decisions (2.M deliverable 20, pure): the type the copy takes, what a
 * refusal marks and the request it sends.
 */
import { ApiError, type DocTypeSummaryV1, type VariantCreateRequestV1 } from '@codex/contracts';
import type { CardKind } from './draft.js';

/** The live canon type whose key is the card's kind (`spell` or `item`), or null. */
export function cardTypeFor(
  kind: CardKind,
  types: readonly DocTypeSummaryV1[],
): DocTypeSummaryV1 | null {
  return (
    types.find(
      (t) =>
        t.key === kind &&
        t.library === 'canon' &&
        !t.archived &&
        t.capabilities.includes('has_card'),
    ) ?? null
  );
}

export const TYPE_NAME: Readonly<Record<CardKind, string>> = { spell: 'Spell', item: 'Item' };

/** "The Spell type isn't available." */
export const typeMissing = (kind: CardKind) => `The ${TYPE_NAME[kind]} type isn't available.`;

export const CARD_CUSTOMIZE_GENERIC = "Couldn't create the copy.";
export const CARD_EMPTY_NAME = 'Enter a name for the copy.';

export interface CardCustomizeFields {
  readonly name: string;
  readonly typeId: string;
  readonly homeId: string | null;
}

export interface CardCustomizeErrors {
  readonly name?: string;
  readonly home?: string;
  /** The dialog's message: what belongs to no field. */
  readonly message?: string;
}

const TYPE_ISSUES: ReadonlySet<unknown> = new Set([
  'kind_mismatch',
  'type_without_card',
  'unknown_type',
]);

/** s1's refusals of a card `variants.create`, by field. */
export function customizeErrors(error: unknown, kind: CardKind): CardCustomizeErrors {
  if (!(error instanceof ApiError)) return { message: CARD_CUSTOMIZE_GENERIC };
  if (error.status === 409 && error.code === 'document_trashed') {
    return { home: 'That page is in the trash.' };
  }
  if (error.status === 404 && error.code === 'not_found') {
    return { message: 'That page is no longer available.' };
  }
  if (error.status === 403 && error.code === 'reference_read_only') {
    return { message: "The SRD entry can't be changed." };
  }
  if (error.status === 400) return { name: 'Enter a name of up to 200 characters.' };
  if (error.status === 422 && error.code === 'invalid_body') {
    const details = error.body.details;
    const issue =
      typeof details === 'object' && details !== null
        ? (details as { issue?: unknown }).issue
        : undefined;
    if (TYPE_ISSUES.has(issue)) return { message: typeMissing(kind) };
  }
  return { message: CARD_CUSTOMIZE_GENERIC };
}

/** The body of `variants.create`; the name is trimmed, no home is left out. */
export function variantRequest(fields: CardCustomizeFields): VariantCreateRequestV1 {
  return {
    title: fields.name.trim(),
    typeId: fields.typeId,
    ...(fields.homeId === null ? {} : { homeId: fields.homeId }),
  };
}
